'use client';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useState, useTransition } from 'react';
import { Auth } from '~/modules/auth/client';
import { action_PortalSwitcher_getData } from './actions';
import { PortalSwitcher_canSwitch } from './mapping';
import { PortalSwitcher_Trigger } from './Trigger';
import type { PortalSwitcher_Data } from './types';

const PortalSwitcher_Panel = dynamic(
    () => import('./PortalSwitcher.client').then(m => m.PortalSwitcher),
    { ssr: false },
);

/** Options survive remounts (client navigations, closing and reopening the panel) for the lifetime
 * of the tab, so hovering the avatar only ever hits the server once. */
const OPTIONS_CACHE = new Map<string, PortalSwitcher_Data>();

/** Deferred version of `PortalSwitcher`: renders only the account avatar until the user reaches for
 * it, then loads the panel chunk and its data.
 *
 * Use this wherever the switcher sits on a page whose loading performance matters (the public
 * website above all) — the panel pulls in cmdk, radix popover and the tenant query, none of which
 * belong in the initial payload of a page nobody opens the switcher on. */
export const PortalSwitcher_Lazy: React.FC<
    Omit<React.ComponentProps<typeof PortalSwitcher_Panel>, 'defaultOpen' | 'initialData'>
> = props => {
    const auth = Auth.useSession();
    const router = useRouter();
    const [isActivated, setIsActivated] = useState(false);
    const [data, setData] = useState<PortalSwitcher_Data | null>(
        () => OPTIONS_CACHE.get(props.locale) ?? null,
    );
    const [, startTransition] = useTransition();

    function activate(options?: { navigateWhenSingle?: boolean }) {
        if (isActivated) return;
        setIsActivated(true);
        if (data) return;

        startTransition(async () => {
            const fetched = await action_PortalSwitcher_getData(props.locale);
            if (!fetched) return;

            OPTIONS_CACHE.set(props.locale, fetched);
            setData(fetched);

            // A click on a single-portal account avatar should still go somewhere, the way the
            // account button it replaced did — we just couldn't know that until the data landed.
            if (options?.navigateWhenSingle === true && !PortalSwitcher_canSwitch(fetched)) {
                router.push(fallbackHref(props.profileHref, fetched));
            }
        });
    }

    if (!auth.user) return null;

    if (isActivated && data && PortalSwitcher_canSwitch(data)) {
        return <PortalSwitcher_Panel {...props} defaultOpen initialData={data} />;
    }

    return (
        <PortalSwitcher_Trigger
            user={auth.user}
            color={props.color}
            className={props.className}
            href={
                data && !PortalSwitcher_canSwitch(data)
                    ? fallbackHref(props.profileHref, data)
                    : undefined
            }
            onPointerEnter={() => activate()}
            onFocus={() => activate()}
            onClick={() => activate({ navigateWhenSingle: true })}
        />
    );
};

function fallbackHref(profileHref: string | undefined, data: PortalSwitcher_Data) {
    return profileHref ?? data.options[0]?.href ?? '/';
}
